export function getRandomColor(brightness: number) {
  // Six levels of brightness from 0 to 5, 0 being the darkest
  var rgb = [Math.random() * 256, Math.random() * 256, Math.random() * 256];
  var mix = [brightness * 51, brightness * 51, brightness * 51]; //51 => 255/5
  var mixedrgb = [rgb[0] + mix[0], rgb[1] + mix[1], rgb[2] + mix[2]].map(
    function (x) {
      return Math.round(x / 2.0);
    }
  );
  return "rgb(" + mixedrgb.join(",") + ")";
}

export function getColorFromUUID(
  uuid: string,
  brightness: number,
  seed: string = "",
  base: number = 0
) {
  // Define primary colors with maximum separation (HSL format)
  const colorSegments = [
    [0, 100, 50], // Red
    [180, 100, 50], // Cyan
    [60, 100, 50], // Yellow
    [240, 100, 50], // Blue
    [120, 100, 50], // Green
    [300, 100, 50], // Magenta
  ];

  // Enhanced hash function
  const hashString = (str: string) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    return Math.abs(hash);
  };

  // Get hash values
  const seedHash = hashString(seed);
  const uuidHash = hashString(uuid);
  const secondaryHash = hashString(uuid.split("").reverse().join(""));

  // Get base color family
  const index = (seedHash + base) % colorSegments.length;
  const baseSegment = colorSegments[index];

  // Color variations based on UUID
  const getVariation = () => {
    const goldenRatio = 0.618033988749895;

    // Generate angles for separation
    const angle1 = (uuidHash * goldenRatio) % 1;
    const angle2 = (secondaryHash * goldenRatio) % 1;

    // More controlled hue variation (120 degrees)
    const hueShift = Math.floor(angle1 * 120) - 60;

    // Controlled saturation variation (65-95 range)
    const saturationBase = 80;
    const saturationShift = angle2 * 30 - 15;

    // Balanced lightness variation (40-70 range)
    const lightnessBase = 55;
    const lightnessShift = (uuidHash % 30) - 15;

    return {
      hue: (baseSegment[0] + hueShift + 360) % 360,
      saturation: Math.min(95, Math.max(65, saturationBase + saturationShift)),
      lightness: Math.min(70, Math.max(40, lightnessBase + lightnessShift)),
    };
  };

  // Get variations
  const variation = getVariation();

  // Balanced HSL to RGB conversion
  const hslToRgb = (h: number, s: number, l: number) => {
    h = h % 360;
    s = Math.min(100, Math.max(0, s)) / 100;
    l = Math.min(100, Math.max(0, l)) / 100;

    // Moderate contrast enhancement
    l = l < 0.5 ? l * 0.9 : l * 1.1;

    const c = (1 - Math.abs(2 * l - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = l - c / 2;

    let r = 0,
      g = 0,
      b = 0;
    if (h < 60) {
      r = c;
      g = x;
      b = 0;
    } else if (h < 120) {
      r = x;
      g = c;
      b = 0;
    } else if (h < 180) {
      r = 0;
      g = c;
      b = x;
    } else if (h < 240) {
      r = 0;
      g = x;
      b = c;
    } else if (h < 300) {
      r = x;
      g = 0;
      b = c;
    } else {
      r = c;
      g = 0;
      b = x;
    }

    return [
      Math.round((r + m) * 255),
      Math.round((g + m) * 255),
      Math.round((b + m) * 255),
    ];
  };

  // Convert to RGB with moderate variation
  let [r, g, b] = hslToRgb(
    variation.hue,
    variation.saturation,
    variation.lightness
  );

  // Add controlled RGB variations
  const rgbVariation = 45; // Moderate variation
  const rShift = ((uuidHash & 0xff) % rgbVariation) - rgbVariation / 2;
  const gShift = ((secondaryHash & 0xff) % rgbVariation) - rgbVariation / 2;
  const bShift = (((uuidHash >> 8) & 0xff) % rgbVariation) - rgbVariation / 2;

  // Apply controlled shifts
  r = Math.min(255, Math.max(30, r + rShift)); // Minimum 30 to avoid too dark
  g = Math.min(255, Math.max(30, g + gShift));
  b = Math.min(255, Math.max(30, b + bShift));

  // Balanced brightness adjustment
  const adjustBrightness = (value: number) => {
    const gamma = 2.2;
    const normalizedBrightness = Math.min(
      1.2,
      Math.max(0.3, (brightness + 1) / 5)
    );
    const gammaCorrection = Math.pow(normalizedBrightness, 1 / gamma);

    // Moderate contrast enhancement
    const contrastEnhanced =
      value < 128
        ? value * (0.85 * gammaCorrection) // Less dark
        : value * (1.15 * gammaCorrection); // Less light

    return Math.round(Math.max(30, Math.min(240, contrastEnhanced))); // Constrain range
  };

  const finalR = adjustBrightness(r);
  const finalG = adjustBrightness(g);
  const finalB = adjustBrightness(b);

  return `rgb(${finalR},${finalG},${finalB})`;
}
export function snakeToCapital(str: string): string {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
